import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useAuth } from "@/features/auth/auth";
import { ApiError } from "@/lib/apiClient";
import { takeReturnTo } from "@/lib/returnTo";
import { Button } from "@/components/ui/button";
import { Form } from "@/components/ui/form";
import { TextField, PasswordField, useAutoFocus } from "@/components/form";
import { Card, CardContent } from "@/components/ui/card";
import { Notice } from "@/components/Notice";
import blogicLogo from "@/assets/blogic-logo.png";

const schema = z.object({
  identifier: z.string().trim().min(1, "Enter your email or username."),
  password: z.string().min(1, "Enter your password."),
});
type LoginForm = z.infer<typeof schema>;

export default function LoginPage() {
  const { signIn } = useAuth();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  const form = useForm<LoginForm>({
    resolver: zodResolver(schema),
    defaultValues: { identifier: "", password: "" },
  });

  useAutoFocus(form, "identifier");

  async function onSubmit(v: LoginForm) {
    setError(null);
    try {
      const user = await signIn(v.identifier, v.password);
      navigate(takeReturnTo(user.userId) ?? "/", { replace: true });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Sign-in failed.");
    }
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-muted px-4 py-10">
      <Card className="w-full max-w-md">
        <CardContent className="p-8">
          <div className="mb-7 flex flex-col items-center text-center">
            <img src={blogicLogo} alt="BLogic Systems" className="h-12 w-auto" />
            <p className="mt-3 text-sm text-muted-foreground">Sign in to your account</p>
          </div>

          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
              <TextField
                control={form.control}
                name="identifier"
                label="Email or username"
                autoComplete="username"
              />
              <PasswordField
                control={form.control}
                name="password"
                label="Password"
                autoComplete="current-password"
              />

              {error && <Notice tone="error">{error}</Notice>}

              <Button type="submit" disabled={form.formState.isSubmitting} className="w-full">
                {form.formState.isSubmitting ? "Signing in…" : "Sign in"}
              </Button>
            </form>
          </Form>
        </CardContent>
      </Card>
    </div>
  );
}
